import React, { useMemo, useState } from 'react';
import { Plus, Search, AlertTriangle } from 'lucide-react';
import TagChip from './TagChip';

const slugify = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// Small edit-distance so the picker can say "did you mean Receiving?" before
// someone creates "Recieving". Bounded input lengths keep this trivial.
const editDistance = (a, b) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const row = [i];
    for (let j = 1; j <= b.length; j += 1) {
      row[j] = Math.min(
        prev[j] + 1,
        row[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
    prev = row;
  }
  return prev[b.length];
};

/**
 * The tag "bucket": every existing tag is offered as a toggle, and a new one is
 * only created when nothing already covers it.
 *
 * `value` is an array of tag objects ({id, name, slug}) or bare name strings
 * for tags that don't exist yet; the server resolves both.
 */
const TagPicker = ({ allTags = [], value = [], onChange, autoFocus = false }) => {
  const [filter, setFilter] = useState('');

  const selectedKeys = useMemo(
    () => new Set(value.map((t) => (t.id ? `id:${t.id}` : `name:${slugify(t.name || t)}`))),
    [value]
  );

  const keyFor = (tag) => (tag.id ? `id:${tag.id}` : `name:${slugify(tag.name || tag)}`);

  const toggle = (tag) => {
    const key = keyFor(tag);
    if (selectedKeys.has(key)) onChange(value.filter((t) => keyFor(t) !== key));
    else onChange([...value, tag]);
  };

  const filterSlug = slugify(filter);

  const visible = useMemo(() => {
    if (!filter.trim()) return allTags;
    const needle = filter.trim().toLowerCase();
    return allTags.filter((t) =>
      t.name.toLowerCase().includes(needle) ||
      (t.aliases || []).some((a) => a.toLowerCase().includes(needle))
    );
  }, [allTags, filter]);

  // An exact hit on a name OR an alias means "create" would be a duplicate.
  const exactMatch = useMemo(
    () => allTags.find((t) =>
      t.slug === filterSlug || (t.aliases || []).some((a) => slugify(a) === filterSlug)),
    [allTags, filterSlug]
  );

  const nearMatches = useMemo(() => {
    if (!filterSlug || exactMatch || filterSlug.length < 3) return [];
    return allTags
      .map((t) => ({ tag: t, distance: editDistance(filterSlug, t.slug) }))
      .filter(({ distance, tag }) =>
        distance <= 2 || tag.slug.includes(filterSlug) || filterSlug.includes(tag.slug))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 3)
      .map(({ tag }) => tag);
  }, [allTags, filterSlug, exactMatch]);

  const canCreate = filterSlug.length > 0 && !exactMatch &&
    !value.some((t) => slugify(t.name || t) === filterSlug);

  const createTag = () => {
    if (!canCreate) return;
    onChange([...value, { name: filter.trim() }]);
    setFilter('');
  };

  return (
    <div className="space-y-3">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((tag) => (
            <TagChip
              key={keyFor(tag)}
              tag={typeof tag === 'string' ? { name: tag } : tag}
              selected
              onRemove={() => toggle(tag)}
            />
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          type="text"
          value={filter}
          autoFocus={autoFocus}
          onChange={(e) => setFilter(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (exactMatch) { toggle(exactMatch); setFilter(''); }
              else createTag();
            }
          }}
          placeholder="Filter tags, or type a new one…"
          className="w-full bg-slate-900 border border-slate-700 rounded-md pl-9 pr-3 py-2 text-sm
            text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {nearMatches.length > 0 && (
        <div className="flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <span className="mr-2">Similar tag{nearMatches.length > 1 ? 's' : ''} already exist:</span>
            <span className="inline-flex flex-wrap gap-1.5 align-middle">
              {nearMatches.map((t) => (
                <TagChip key={t.id} tag={t} size="xs" onClick={() => { toggle(t); setFilter(''); }} />
              ))}
            </span>
          </div>
        </div>
      )}

      {canCreate && (
        <button
          type="button"
          onClick={createTag}
          className="inline-flex items-center gap-1.5 text-xs text-indigo-300 hover:text-indigo-200"
        >
          <Plus className="w-3.5 h-3.5" />
          Create tag &ldquo;{filter.trim()}&rdquo;
        </button>
      )}

      <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto pr-1">
        {visible.map((tag) => (
          <TagChip
            key={tag.id}
            tag={tag}
            size="xs"
            count={tag.usageCount}
            selected={selectedKeys.has(keyFor(tag))}
            onClick={toggle}
          />
        ))}
        {!visible.length && (
          <p className="text-xs text-slate-500">
            {allTags.length ? 'No tags match that filter.' : 'No tags yet — type above to create the first one.'}
          </p>
        )}
      </div>
    </div>
  );
};

export default TagPicker;
